// src/shared/http/streaming.js
//
// Extracted from the original http-common.js.
// Browser-native fetch wrappers with token injection, auth error handling,
// and streaming (SSE / chunked transfer) support.

import { CONFIG } from './api-client';
import { TokenService } from './token';
import { IframeUtil } from './iframe';
import { AuthErrorHandler } from './error-handler';

// Lazy-load ToastService so this module stays decoupled from PrimeVue.
const showToast = (severity, summary, detail) => {
  import('@/shared/composables/useToast')
    .then(({ showToast: notify }) => notify(severity, summary, detail))
    .catch(() => {});
};

/**
 * Enhanced fetch() wrapper with auth handling.
 *
 * @param {string} url          - Endpoint path (relative to SERVER_PATH) or absolute URL.
 * @param {object} [options]    - Standard fetch RequestInit options.
 * @param {object} [extra]      - Additional behaviour flags.
 * @param {boolean} [extra.handleAuthErrors=true]
 * @param {boolean} [extra.showNetworkErrors=true]
 * @returns {Promise<Response>}
 */
export async function httpFetch(url, options = {}, extra = {}) {
  const { handleAuthErrors = true, showNetworkErrors = true } = extra;

  // Pre-flight: reject immediately if the token is already expired.
  if (handleAuthErrors && TokenService.isTokenExpired()) {
    if (IframeUtil.isIframe()) {
      IframeUtil.postTokenExpired();
    } else {
      AuthErrorHandler.handleAuthError();
    }
    throw new Error('Authentication token expired');
  }

  // Build full URL — allow absolute URLs to pass through unchanged.
  const fullUrl = url.startsWith('http')
    ? url
    : CONFIG.SERVER_PATH + url.replace(/^\//, '');

  // Merge auth header into the caller's headers.
  const headers = { ...(options.headers || {}) };
  const token = TokenService.getToken();
  if (token) {
    headers[CONFIG.AUTH_HEADER] = CONFIG.BEARER_PREFIX + token;
  }

  // Let the browser set Content-Type for FormData uploads.
  const isFormData = options.body instanceof FormData;
  if (
    !isFormData &&
    !headers['Content-Type'] &&
    !(options.method && options.method.toUpperCase() === 'GET')
  ) {
    headers['Content-Type'] = 'application/json';
  }

  const fetchOptions = { ...options, headers, credentials: 'include' };

  try {
    const response = await fetch(fullUrl, fetchOptions);

    if (
      handleAuthErrors &&
      (response.status === 401 || response.status === 403)
    ) {
      AuthErrorHandler.handleAuthError();
      throw new Error('Authentication error');
    }

    return response;
  } catch (error) {
    if (error.name === 'AbortError') throw error;

    if (showNetworkErrors && error.name === 'TypeError') {
      if (!IframeUtil.isIframe()) {
        showToast(
          'error',
          'Network Error',
          'Unable to connect to the server. Please check your internet connection.',
        );
      } else {
        IframeUtil.debounceNotify('ERROR', {
          message: 'Network error. Please check your connection.',
        });
      }
    }

    throw error;
  }
}

/**
 * Streaming fetch — reads the response body as a stream and calls onChunk
 * for each decoded text segment.
 *
 * @param {string}   url       - Endpoint path or absolute URL.
 * @param {object}   options   - fetch RequestInit (signal is wired automatically).
 * @param {Function} onChunk   - Called with each decoded text chunk.
 * @param {Function} [onDone]  - Called when the stream ends normally.
 * @param {Function} [onError] - Called on non-abort errors.
 * @returns {{ abort: Function }} Controller with an abort() method.
 */
export function streamFetch(url, options = {}, onChunk, onDone, onError) {
  const controller =
    options.signal instanceof AbortSignal ? null : new AbortController();
  const signal =
    options.signal instanceof AbortSignal ? options.signal : controller.signal;

  const fetchOptions = {
    ...options,
    signal,
    headers: { ...options.headers },
  };

  (async () => {
    try {
      const response = await httpFetch(url, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();

          if (done) {
            if (buffer.trim()) onChunk(buffer);
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          if (lines.length > 0) {
            onChunk(lines.join('\n'));
          }
        }

        if (onDone) onDone();
      } catch (readError) {
        if (readError.name !== 'AbortError') throw readError;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;

      if (onError) {
        onError(error);
      } else {
        if (!IframeUtil.isIframe()) {
          showToast(
            'error',
            'Stream Error',
            'An error occurred while streaming data.',
          );
        } else {
          IframeUtil.debounceNotify('ERROR', {
            message: 'An error occurred while streaming data.',
          });
        }
        console.error('Stream error:', error);
      }
    }
  })();

  return {
    abort: () => {
      if (controller) controller.abort();
    },
  };
}
